import { Button, Col, Form, Row, Space, theme, Typography } from "antd";
import { useEffect, useState } from "react";
import Data from "../controls/Data";
import { retry } from "../utils/http";
import { DataAction, DataColumn, DataRequest } from "../controls/Data.default";
import { SearchQuery } from "../code/search-query/search-query";
import { getRoadmaps, getCompanies, getRoadmapCompanies } from "../repository/data.repository";
import { assignmentColumns, mapAssignmentQuery } from "./ProcessesView.default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import DynamicForm from "../controls/DynamicForm";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface ProcessAssignmentsViewProps {
  readonly trigger?: string;
  readonly title: string;
  readonly panel: React.ReactNode;
  readonly onAction?: (key: string, record: any) => void;
  readonly onSearch?: (searchQuery: SearchQuery) => void;
}

const roadmapsRequest = createSearchRequest(getRoadmaps, "name");
const companiesRequest = createSearchRequest(getCompanies, "name");

// The operations surface: a flat grid of company assignments (across all roadmaps) with a
// roadmap/company filter sidebar. Each row opens the full-page assignment Manage surface.
function ProcessAssignmentsView(props: ProcessAssignmentsViewProps) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const permissions = useSelector(authorizeSelectors.permissions);

  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<{ readonly searchQuery: SearchQuery }>({
    searchQuery: { conditions: [] },
  });
  const [valuesValid, setValuesValid] = useState(false);

  useEffect(() => {
    setValuesValid(!form.getFieldsError().some(({ errors }) => errors.length));
  }, [values]);

  useEffect(() => {
    setContext((c) => ({ ...c }));
  }, [props.trigger]);

  function onSearchData(): void {
    const searchQuery = mapAssignmentQuery(values);
    setContext({ searchQuery });
    props.onSearch?.(searchQuery);
  }

  function onClearData(): void {
    setValuesValid(true);
    form.resetFields();
    setContext({ searchQuery: { conditions: [] } });
  }

  function onAction(key: string, record: any): void {
    props.onAction?.(key, record);
  }

  const request = ({ context, pageNumber, pageSize, sort }: DataRequest) => {
    const sortBy = (sort || [])[0]?.key ?? null;
    const sortDescending = (sort || [])[0]?.direction === "desc";
    const { searchQuery } = context;
    return retry(() =>
      getRoadmapCompanies({
        searchQuery,
        pageNumber,
        pageSize,
        ...(sortBy ? { sortBy, sortDescending } : {}),
      }),
    ).then(({ data, totalRecords }) => ({ data, pageNumber, pageSize, total: totalRecords }));
  };

  const columns: DataColumn[] = assignmentColumns;
  const actions: DataAction[] = [
    { key: "manage", label: "Manage", handler: onAction },
    {
      key: "delete",
      label: "Delete",
      danger: true,
      disabled: !permissions.can("ProcessesAssignment", "delete"),
      handler: onAction,
    },
  ];

  const filterControls = [
    { type: "combobox" as const, name: "roadmap", label: "Roadmap", request: roadmapsRequest },
    { type: "combobox" as const, name: "company", label: "Company", request: companiesRequest },
  ];

  return (
    <Col className="flex flex-auto flex-col flex-nowrap overflow-hidden">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4 pl-0 pr-0">
        <Typography.Title className="m-0" level={4}>
          {props.title}
        </Typography.Title>
        <Row className="flex flex-auto flex-row">{props.panel}</Row>
      </Row>
      <Row className="flex flex-auto flex-row flex-nowrap overflow-hidden">
        <Col
          className="mr-4 flex flex-col border border-solid pl-2 pr-2"
          style={{ borderColor: token.colorBorder, borderRadius: token.borderRadius }}
        >
          <Space className="flex flex-row justify-end pb-2 pt-2">
            <Button disabled={loading} onClick={onClearData}>
              Clear
            </Button>
            <Button
              type="primary"
              loading={loading}
              disabled={!valuesValid}
              onClick={onSearchData}
            >
              Search
            </Button>
          </Space>
          <DynamicForm
            className="w-72 overflow-y-auto"
            form={form}
            schema={{ groups: [{ controls: filterControls }] }}
          />
        </Col>
        <Data
          columns={columns}
          context={context}
          pageSize={25}
          actions={actions}
          request={request}
          rowKey={({ uid }) => uid}
          onLoading={setLoading}
          onDbSelect={(_key, record) => onAction("manage", record)}
        />
      </Row>
    </Col>
  );
}

export default ProcessAssignmentsView;
